import {
  FolderPlusIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';

import { DropdownItem } from '@/components/ui/catalyst/dropdown';
import {
  useCreateChildCollectionMutation,
  useDestroyCollectionMutation,
  useUpdateCollectionMutation,
} from '@/services/api/collectionService';
import { CollectionNode } from '@/services/types/collection-types';

import { TreeViewSize } from '../constants/treeStyles';
import { ActionDialog } from './ActionDialog';
import { ActionsMenu } from './ActionsMenu';

interface FolderActionsProps {
  node: CollectionNode;
  isRoot: boolean;
  rootNodeId: string;
  onSuccess: () => void;
  size?: TreeViewSize;
}

export const FolderActions = ({
  node,
  isRoot,
  rootNodeId,
  onSuccess,
  size = 'sm',
}: FolderActionsProps) => {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isNewFolderDialogOpen, setIsNewFolderDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [folderName, setFolderName] = useState(node.name);
  const [newFolderName, setNewFolderName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [createChildCollection] = useCreateChildCollectionMutation();
  const [updateCollection] = useUpdateCollectionMutation();
  const [destroyCollection] = useDestroyCollectionMutation();

  const handleRename = async () => {
    if (!folderName.trim()) return;

    try {
      await updateCollection({
        matterId: node.matter_id,
        collectionId: node.id,
        name: folderName.trim(),
      }).unwrap();
      setIsRenameDialogOpen(false);
      onSuccess();
    } catch (e) {
      setError('Failed to rename folder');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      await createChildCollection({
        matterId: node.matter_id,
        parentId: node.id,
        name: newFolderName.trim(),
      }).unwrap();
      setNewFolderName('');
      setIsNewFolderDialogOpen(false);
      onSuccess();
    } catch (e) {
      setError('Failed to create folder');
    }
  };

  const handleDelete = async () => {
    try {
      await destroyCollection({
        matterId: node.matter_id,
        collectionId: node.id,
      }).unwrap();
      setIsDeleteDialogOpen(false);

      onSuccess();
    } catch (e) {
      setError('Failed to delete folder');
    }
  };

  const handleActionClick = (
    e: React.MouseEvent<HTMLElement>,
    action: () => void,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    action();
  };

  const handleKeyDown = (e: React.KeyboardEvent<Element>) => {
    e.stopPropagation();
  };

  return (
    <>
      <ActionsMenu onKeyDown={handleKeyDown}>
        {!isRoot && (
          <DropdownItem
            onClick={(e: React.MouseEvent<HTMLElement>) =>
              handleActionClick(e, () => setIsRenameDialogOpen(true))
            }
          >
            <PencilIcon data-slot="icon" />
            Rename
          </DropdownItem>
        )}
        <DropdownItem
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            handleActionClick(e, () => setIsNewFolderDialogOpen(true))
          }
        >
          <FolderPlusIcon data-slot="icon" />
          New Subfolder
        </DropdownItem>
        {!isRoot && (
          <DropdownItem
            onClick={(e: React.MouseEvent<HTMLElement>) =>
              handleActionClick(e, () => setIsDeleteDialogOpen(true))
            }
          >
            <TrashIcon data-slot="icon" className="text-red-500" />
            <span className="text-red-600">Delete</span>
          </DropdownItem>
        )}
      </ActionsMenu>

      {/* Rename Dialog */}
      <ActionDialog
        isOpen={isRenameDialogOpen}
        onClose={() => setIsRenameDialogOpen(false)}
        title="Rename Folder"
        description="Enter a new name for the folder."
        confirmButtonText="Rename"
        onConfirm={handleRename}
        errorMessage={error}
        inputValue={folderName}
        onInputChange={setFolderName}
        isConfirmDisabled={!folderName.trim()}
      />

      {/* New Folder Dialog */}
      <ActionDialog
        isOpen={isNewFolderDialogOpen}
        onClose={() => setIsNewFolderDialogOpen(false)}
        title="Create New Folder"
        description="Enter a name for the new folder."
        confirmButtonText="Create"
        onConfirm={handleCreateFolder}
        errorMessage={error}
        inputValue={newFolderName}
        onInputChange={setNewFolderName}
        inputPlaceholder="Folder name"
        isConfirmDisabled={!newFolderName.trim()}
      />

      {/* Delete Dialog */}
      <ActionDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Delete Folder"
        description={`Are you sure you want to delete "${node.name}"? This action cannot be undone and will delete all files within this folder.`}
        confirmButtonText="Delete"
        onConfirm={handleDelete}
        isDangerousAction
      />
    </>
  );
};
